import {Trans} from '@lingui/macro';
import produce, {castDraft} from 'immer';
import {omit} from 'lodash';
import React, {FC, ReactNode, useMemo} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {isDefined, OrNull} from '../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {useConstant} from '../../../../../srcCommon/helpers/hookHelpers';
import {computeIsGlobalNamespace} from '../../../../helpers/models/namespaceHelpers';
import {isCirclePublicId} from '../../../../models/api/circleModel';
import {useBaseEditorContext} from '../../../editors/baseEditorContext';
import {EnumParameter} from '../../../editors/commons/parameterModel';
import {TeammateRef} from '../../../editors/commons/paramModel';
import {
  isExpressionRef,
  resolveExpression,
  WithExpressionRef,
} from '../../../editors/expressions/expressionRefModel';
import {useExpressionsContext} from '../../../editors/expressions/expressionsContext';
import {
  isListType,
  TeammateListResult,
  TeammateResult,
} from '../../../editors/expressions/infer/expressionResultModel';
import {useRuleEditorContext} from '../../../editors/ruleEditor/ruleEditorContext';
import {RuleDefaultCardTypesEnum} from '../../ruleActionConditionHelpers';
import {AssignAction, RandomAssignAction, RuleAction, RuleActionTypesEnum} from '../../ruleActionModel';
import {RuleActionDefinition} from '../../ruleParameterModel';
import {useOnSelectCreateExpressionRef} from '../hooks/useOnSelectCreateExpression';
import {TeammateSelectedDropdownItem, TeammatesParameter} from '../parameters/teammateParameter';
import {RuleActionIcon} from '../ruleActionIcon';
import {
  MultilineRuleParameterCardDefaultTitleStyled,
  RuleMultilineParameterCard,
} from '../ruleMultilineParameterCard';
import {renderGenericRuleParameters} from './ruleDefaultActionConditionCard';

/*
 * Types.
 */

type AnyAssignAction = AssignAction | RandomAssignAction;

interface RuleAssignToTeammatesActionCardProps {
  action: AnyAssignAction;
  actionDefinition: RuleActionDefinition | undefined;
  isDisabled?: boolean;
  interactionId?: string;
  errorMessage?: ReactNode;
  onUpdate: HandlerOf<RuleAction>;
  onDelete?: HandlerOf<RuleAction>;
}

type AssignAlgoEnum = RandomAssignAction['params']['algo'];

/*
 * Constants.
 */

const randomAssign = RuleActionTypesEnum.RANDOM_ASSIGN;
const assign = RuleActionTypesEnum.ASSIGN;
const assignAlgoParam = 'algo';

/*
 * Styles.
 */

const StyledAssignToContentDiv = styled.div`
  display: grid;
  grid-template-columns: 40% auto;
  grid-template-rows: 30px;
`;

/*
 * Component.
 */

// A card that groups 'assign' (1 teammate) and 'random_assign' (2+ teammates).
export const RuleAssignToTeammatesActionCard: FC<RuleAssignToTeammatesActionCardProps> = (props) => {
  const {action, actionDefinition, isDisabled, interactionId, errorMessage, onUpdate, onDelete} = props;

  const {namespace} = useBaseEditorContext();
  // @TODO: Find a way to avoid using `useRuleEditorContext`.
  const {isLibrary, ruleIntegration} = useRuleEditorContext();
  const {expressions} = useExpressionsContext();

  const canChangeType = useConstant(() => !(isLibrary && action.type === randomAssign));

  const randomAssignActionDefinition = ruleIntegration.findActionDefinition({
    type: randomAssign,
    integration_id: 0,
  });
  const assignAlgoEnum = randomAssignActionDefinition?.parameters?.[assignAlgoParam] as
    | EnumParameter
    | undefined;

  const shouldDisableLoadBalancing = useMemo(() => {
    if (computeIsGlobalNamespace(namespace)) {
      return true;
    }

    const shouldDisableForParam = (param: WithExpressionRef<TeammateRef>): boolean =>
      isExpressionRef(param) || isCirclePublicId(param);

    switch (action.type) {
      case RuleActionTypesEnum.ASSIGN: {
        if (!action.params?.teammate) {
          return false;
        }

        return shouldDisableForParam(action.params.teammate);
      }

      case RuleActionTypesEnum.RANDOM_ASSIGN: {
        if (!action.params?.teammates) {
          return false;
        }

        return action.params.teammates.some(shouldDisableForParam);
      }

      default:
        return false;
    }
  }, [action.params, action.type, namespace]);

  const algoParam = useMemo(() => {
    if (!actionDefinition || !actionDefinition?.parameters?.algo) {
      return {};
    }

    if (!shouldDisableLoadBalancing) {
      return actionDefinition.parameters.algo;
    }

    return removeLoadBalancingFromAlgoParam(actionDefinition.parameters.algo);
  }, [actionDefinition, shouldDisableLoadBalancing]);

  const onTeammatesChange = (teammateIdRefs: OrNull<ReadonlyArray<TeammateSelectedDropdownItem>>) => {
    if (!teammateIdRefs || !actionDefinition || !assignAlgoEnum) {
      return;
    }

    onUpdate(
      produce(action, (draft: AnyAssignAction) => {
        const draftAction = castDraft(draft);
        const expression = resolveExpression(teammateIdRefs[0], expressions);
        const hasCircle = teammateIdRefs.some((teammateIdRef) => isCirclePublicId(teammateIdRef));
        const isMultipleSelection =
          teammateIdRefs.length > 1 || (expression && isListType(expression.inferredType)) || hasCircle;

        /*
         * Assign to single teammate.
         * We allow switching from random_assign to plain single teammate assign
         * since it's a simpler action that is also more accessible.
         * (random assign being only available for pro/growth+ plans)
         */
        if (!isMultipleSelection && canChangeType) {
          draftAction.type = assign;
          draftAction.params = {
            ...omit(draftAction.params, 'teammates', 'algo'),
            teammate: teammateIdRefs[0],
          };
          return;
        }

        const overrideLoadBalancing = teammateIdRefs.some(isExpressionRef) || hasCircle;

        // Assign in group
        let algo =
          draftAction.type === randomAssign
            ? draftAction.params.algo
            : (assignAlgoEnum.default as AssignAlgoEnum);

        // Disable load balancing algo with expressions.
        if (overrideLoadBalancing && algo === 'load_balancing') {
          algo = assignAlgoEnum.default as AssignAlgoEnum;
        }

        draftAction.type = randomAssign;
        draftAction.params = {
          ...omit(draftAction.params, 'teammate'),
          teammates: castDraft(teammateIdRefs),
          algo,
        };
      }),
    );
  };

  const teammateIdRefs = (action.type === assign ? [action.params.teammate] : action.params.teammates).filter(
    isDefined,
  );

  const onSelectCreateExpression = useOnSelectCreateExpressionRef(
    (expressionRef) => onTeammatesChange([...teammateIdRefs, expressionRef]),
    [TeammateResult, TeammateListResult],
  );

  // This should not happen, but safety check.
  const errorLabel = <Trans id="manifest-action-not-found">Action {action.type} not found</Trans>;
  if (!actionDefinition || !assignAlgoEnum) {
    return <RuleMultilineParameterCard icon={<RuleActionIcon id={action.type} />} title={errorLabel} />;
  }

  const title = (
    <>
      <MultilineRuleParameterCardDefaultTitleStyled>
        <Trans id="rule-teammate-action-card-assign-to">Assign to</Trans>
      </MultilineRuleParameterCardDefaultTitleStyled>
      <TeammatesParameter
        selectedItems={teammateIdRefs}
        expressions={expressions}
        isDisabled={isDisabled}
        onChange={onTeammatesChange}
        onSelectCreateExpression={onSelectCreateExpression}
      />
    </>
  );

  const onCancel = onDelete && !isDisabled ? () => onDelete(action) : undefined;

  const hasAdditionalParameters = canChangeType
    ? action.type === randomAssign && teammateIdRefs.length > 0
    : action.type === randomAssign;

  const definitionParameters = {
    ...actionDefinition.parameters,
    algo: algoParam,
  };

  // Generic rendering is used for the leftover parameters.
  const {parameters, collapsableParameters} = renderGenericRuleParameters(
    action,
    actionDefinition,
    RuleDefaultCardTypesEnum.ACTION,
    onUpdate,
    omit(definitionParameters, 'teammate', 'teammates'),
    isDisabled,
  );

  return (
    <RuleMultilineParameterCard
      interactionId={interactionId}
      icon={<RuleActionIcon id={action.type} />}
      title={title}
      onCancel={onCancel}
      collapsableParameters={collapsableParameters}
      errorMessage={errorMessage}
    >
      {hasAdditionalParameters && <StyledAssignToContentDiv>{parameters}</StyledAssignToContentDiv>}
    </RuleMultilineParameterCard>
  );
};

/*
 * Helpers.
 */

const removeLoadBalancingFromAlgoParam = (algo: any) => {
  const loadBalancingIndex = algo.enum.indexOf('load_balancing');
  if (loadBalancingIndex === -1) {
    return algo;
  }

  return {
    ...algo,
    enum: algo.enum.filter((_enum: string, i: number) => i !== loadBalancingIndex),
    enumTitles: algo.enumTitles.filter((_enumTitles: string, i: number) => i !== loadBalancingIndex),
    enumDescriptions: algo.enumDescriptions.filter(
      (_enumDescriptions: string, i: number) => i !== loadBalancingIndex,
    ),
  };
};
